import { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { isPresent } from "neetocist";
import { dayjs } from "neetocommons/utils";
import { Help } from "neetoicons";
import { Typography, Modal, Button, Input, Select, DatePicker } from "neetoui";
import { prop } from "ramda";
import { useTranslation } from "react-i18next";

const RegenerateURL = ({
  isOpen,
  onClose,
  url,
  isLoading,
  handleRegenerate,
  regenerateConfig = { expiresInOptions: [], helpUrl: null },
}) => {
  const { t } = useTranslation();

  const prevIsLoadingRef = useRef(isLoading);
  const regenerateStrategy = isPresent(regenerateConfig?.expiresInOptions)
    ? "expiryBased"
    : "basic";

  const defaultExpiresInOption = regenerateConfig?.expiresInOptions?.[0]?.value;
  const [selectedExpiresInOption, setSelectedExpiresInOption] = useState(
    defaultExpiresInOption
  );

  const defaultExpiresOnDate = dayjs().add(10, "day").toDate();
  const [selectedExpiresOnDate, setSelectedExpiresOnDate] =
    useState(defaultExpiresOnDate);

  const isSelectedExpiresInOptionNever = selectedExpiresInOption === "never";
  const isSelectedExpiresInOptionCustom = selectedExpiresInOption === "custom";

  const isSelectedExpiresInOptionValidNumberOfDays =
    isPresent(selectedExpiresInOption) &&
    !isSelectedExpiresInOptionCustom &&
    !isSelectedExpiresInOptionNever;

  const isValidExpiryConfig =
    isSelectedExpiresInOptionNever ||
    (isSelectedExpiresInOptionCustom && isPresent(selectedExpiresOnDate)) ||
    isSelectedExpiresInOptionValidNumberOfDays;

  const expiryConfigPayload = isSelectedExpiresInOptionValidNumberOfDays
    ? { expiresInDays: selectedExpiresInOption }
    : { expiresOnDate: selectedExpiresOnDate };

  const onSubmit = () =>
    regenerateStrategy === "basic" || isSelectedExpiresInOptionNever
      ? handleRegenerate()
      : handleRegenerate({ payload: expiryConfigPayload });

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) onClose();

    prevIsLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedExpiresInOption(defaultExpiresInOption);
    setSelectedExpiresOnDate(defaultExpiresOnDate);
  }, [isOpen]);

  return (
    <Modal
      {...{ isOpen, onClose }}
      data-testid="regenerate-url-modal"
      size={regenerateStrategy === "basic" ? "medium" : "large"}
    >
      <Modal.Header>
        <Typography lineHeight="normal" style="h2" weight="semibold">
          {regenerateStrategy === "expiryBased"
            ? t(
                "neetoMolecules.shareViaLink.regenerateUrl.titleForExpiryBasedRegeneration"
              )
            : t("neetoMolecules.shareViaLink.regenerateUrl.title")}
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="flex w-full flex-col gap-y-6">
          {regenerateStrategy === "basic" && (
            <Input
              disabled
              label={t("neetoMolecules.shareViaLink.regenerateUrl.inputLabel")}
              value={url}
            />
          )}
          {regenerateStrategy === "expiryBased" && (
            <div className="flex flex-row gap-2">
              <Select
                data-testid="expires-in-select"
                defaultValue={regenerateConfig.expiresInOptions?.[0]}
                options={regenerateConfig.expiresInOptions}
                className={classNames({
                  "w-1/2": isSelectedExpiresInOptionCustom,
                  "w-full": !isSelectedExpiresInOptionCustom,
                })}
                label={t(
                  "neetoMolecules.shareViaLink.regenerateUrl.expiresInLabel"
                )}
                labelProps={{
                  helpIconProps: isPresent(regenerateConfig?.helpUrl) && {
                    icon: Help,
                    className:
                      "cursor-pointer opacity-75 hover:opacity-100 transition-opacity duration-300 ease-in-out",
                    popoverProps: {
                      title: t(
                        "neetoMolecules.shareViaLink.regenerateUrl.expiresInHelpTitle"
                      ),
                      helpLinkProps: {
                        label: t(
                          "neetoMolecules.shareViaLink.regenerateUrl.helpLinkLabel"
                        ),
                        href: regenerateConfig?.helpUrl,
                        target: "_blank",
                      },
                    },
                  },
                }}
                onChange={option => setSelectedExpiresInOption(option.value)}
              />
              {isSelectedExpiresInOptionCustom && (
                <DatePicker
                  data-testid="expires-on-date-picker"
                  dateFormat={globalProps?.user?.dateFormat ?? undefined}
                  disabledDate={d => !d || d.isBefore(dayjs())}
                  getPopupContainer={prop("parentNode")}
                  picker="date"
                  type="date"
                  value={selectedExpiresOnDate}
                  label={t(
                    "neetoMolecules.shareViaLink.regenerateUrl.expiresOnLabel"
                  )}
                  onChange={setSelectedExpiresOnDate}
                />
              )}
            </div>
          )}
          <Typography
            className="neeto-ui-text-gray-700"
            lineHeight="normal"
            style="body2"
          >
            {t("neetoMolecules.shareViaLink.regenerateUrl.description")}
          </Typography>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center gap-x-2">
        <Button
          data-testid="regenerate-url-confirm-button"
          loading={isLoading}
          disabled={
            regenerateStrategy === "expiryBased" && !isValidExpiryConfig
          }
          label={t(
            "neetoMolecules.shareViaLink.regenerateUrl.submitButtonLabel"
          )}
          onClick={onSubmit}
        />
        <Button
          data-testid="regenerate-url-cancel-button"
          style="text"
          label={t(
            "neetoMolecules.shareViaLink.regenerateUrl.cancelButtonLabel"
          )}
          onClick={onClose}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default RegenerateURL;
